import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, cmMetricCases, createCaseMgmtApi, createUsersApi, CM_CATEGORIES, CM_CLOSED_PAGE_MAX, CM_CLOSED_WINDOWS, CM_EXPORT_MAX, CM_METRIC_DIMS, CM_SECTIONS_PAGE_MAX } from '@bloo-team/api-client'
import type {
  CaseMgmtApi, CmBulkCloseInput, CmBulkResult, CmCase, CmCaseMetrics, CmCategory, CmCreateInput, CmDisposition, CmDispositionChange, CmListQuery, CmPage, CmScope,
  CmStatus, CmTransitionInput, CmUpdateInput, CmWorkspace,
  CmCaseExport, CmClosedCase, CmClosedWindow,
} from '@bloo-team/api-client'
import { client, isDemoSession } from '@bloo-team/core-shell'
import type { DqlTime } from '@bloo-team/api-client'
import { checkCasesRange, rangeBounds, rangeKey } from './cases-range'
import { DEMO_ANALYSTS, demoCaseMgmtApi } from './case-mgmt-demo'

/**
 * Vantage · Cases (V2) — data hooks over the case-management service
 * (api-client case-mgmt.ts), with the demo/live switch every Vantage
 * screen uses: demo mode talks to the in-memory service in
 * case-mgmt-demo.ts through the same CaseMgmtApi, so the screens never
 * branch on the mode.
 */

const liveApi = createCaseMgmtApi(client)
const liveUsersApi = createUsersApi(client)
const api = (): CaseMgmtApi => (isDemoSession() ? demoCaseMgmtApi : liveApi)
const mode = () => (isDemoSession() ? 'demo' : 'live')
const baseKey = (s: CmScope) => ['cm', s.clusterId, s.scopeId] as const

/* ------------------------------------------------------------------ model */

export const CATEGORY_LABEL: Record<CmCategory, string> = {
  new: 'New',
  in_progress: 'In progress',
  on_hold: 'On hold',
  closed: 'Closed',
}

/** How a closed case ended (Epic 20), as the service's contract labels it. */
export const DISPOSITION_LABEL: Record<CmDisposition, string> = {
  FALSE_POSITIVE: 'False Positive',
  TRUE_POSITIVE: 'True Positive',
  TRUE_NEGATIVE: 'True Negative',
  OTHER: 'Other',
}

/** A case's disposition in words: none set (not closed, or closed before Epic 20) reads Not set; an unknown value as is. */
export const dispositionLabel = (v: string | null | undefined): string => (v ? (DISPOSITION_LABEL[v as CmDisposition] ?? v) : 'Not set')

/** Severity as the console's severity system spells it (data-severity). */
export const sevKey = (s: string | null | undefined) => {
  const k = (s ?? 'info').toLowerCase()
  return k === 'informational' ? 'info' : k
}

/** MTTA / MTTR windows; the status counts are all-time. */
export const METRIC_WINDOWS = ['24h', '7d', '30d', '90d'] as const
export type MetricWindow = (typeof METRIC_WINDOWS)[number]
const WINDOW_MS: Record<MetricWindow, number> = { '24h': 86_400_000, '7d': 7 * 86_400_000, '30d': 30 * 86_400_000, '90d': 90 * 86_400_000 }

/** `2h 14m`, `1d 4h`, `45s` — the two largest non-zero units; `—` for null. */
export function durationLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—'
  if (seconds > 0 && seconds < 1) return '<1s'
  let left = Math.max(0, Math.round(seconds))
  const parts: string[] = []
  for (const [u, size] of [['d', 86_400], ['h', 3_600], ['m', 60], ['s', 1]] as const) {
    const n = Math.floor(left / size)
    left -= n * size
    if (n > 0 || parts.length) parts.push(`${n}${u}`)
    if (parts.length === 2) break
  }
  return parts.length ? parts.join(' ').replace(/ 0[a-z]$/, '') : '0s'
}

/** `3h ago`, `2d ago` — coarse, for list rows. */
export function agoLabel(at: number | null, now = Date.now()): string {
  if (at === null) return '—'
  const s = Math.max(0, Math.round((now - at) / 1000))
  if (s < 60) return 'just now'
  if (s < 3_600) return `${Math.floor(s / 60)}m ago`
  if (s < 86_400) return `${Math.floor(s / 3_600)}h ago`
  return `${Math.floor(s / 86_400)}d ago`
}

/** `3f9c…a21e`. */
export const shortId = (id: string) => (id.length > 12 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id)

/** `case-23` — how a case is named on screen; `—` from a service build without numbers. The UUID is never shown. */
export const caseLabel = (c: { caseNumber: string | null }) => c.caseNumber ?? '—'

/** The case page's path id: `case-N`, else the UUID (the service's case paths take either). */
export const caseRef = (c: { id: string; caseNumber: string | null }) => c.caseNumber ?? c.id

/** Listing columns that sort the loaded page (the service's order otherwise). */
export const CASE_SORT_KEYS = ['number', 'severity', 'title', 'status', 'tta', 'created', 'updated'] as const
export type CaseSortKey = (typeof CASE_SORT_KEYS)[number]
export interface CaseSort {
  key: CaseSortKey
  dir: 'asc' | 'desc'
}

/** A header click: descending first, then ascending, then back to the service's order (null). */
export const nextCaseSort = (cur: CaseSort | null, key: CaseSortKey): CaseSort | null =>
  cur?.key !== key ? { key, dir: 'desc' } : cur.dir === 'desc' ? { key, dir: 'asc' } : null

const SEVERITY_RANK: Record<string, number> = { informational: 1, low: 2, medium: 3, high: 4, critical: 5 }

/**
 * The loaded page in a column's order — severity by rank, status by category
 * then the catalogue's order; a missing value (no number, no TTA) sorts last
 * either way; ties keep the service's order.
 */
export function sortCases(items: readonly CmCase[], sort: CaseSort | null, statuses: readonly CmStatus[]): CmCase[] {
  if (!sort) return [...items]
  const statusRank = (key: string) => {
    const st = statuses.find((x) => x.key === key)
    return st ? CM_CATEGORIES.indexOf(st.category) * 10_000 + st.order : null
  }
  const value = (c: CmCase): number | string | null => {
    switch (sort.key) {
      case 'number':
        return c.caseNumber ? Number(c.caseNumber.slice(5)) : null
      case 'severity':
        return SEVERITY_RANK[c.severity] ?? 0
      case 'title':
        return c.title.toLowerCase()
      case 'status':
        return statusRank(c.status)
      case 'tta':
        return c.ttaSeconds
      case 'created':
        return c.createdAt
      case 'updated':
        return c.updatedAt
    }
  }
  const sign = sort.dir === 'asc' ? 1 : -1
  return items
    .map((c, i) => ({ c, i, v: value(c) }))
    .sort((a, b) => {
      if (a.v === null || b.v === null) return a.v === b.v ? a.i - b.i : a.v === null ? 1 : -1
      const d = typeof a.v === 'string' ? a.v.localeCompare(b.v as string) : a.v - (b.v as number)
      return d * sign || a.i - b.i
    })
    .map((x) => x.c)
}

/** A status label from the catalogue; an unknown or archived key shows as itself. */
/**
 * A status key as the catalogue names it today: the built-in New was `OPEN`
 * before Epic 18, and history written then (the audit log) still says so.
 */
export const currentStatusKey = (key: string): string => (key === 'OPEN' ? 'NEW' : key)

export function statusLabel(statuses: readonly CmStatus[] | undefined, key: string): string {
  const k = currentStatusKey(key)
  return statuses?.find((s) => s.key === k)?.label ?? k
}

/* ------------------------------------------------------------------ reads */

/**
 * A page of cases. With `range`, only the cases created in it — bounds taken
 * as the statistics take them (rangeBounds at read time), so the list and the
 * case-metrics counts describe the same cases.
 */
export function useCmCases(s: CmScope, q: CmListQuery, enabled = true, range?: DqlTime) {
  return useQuery<CmPage<CmCase>>({
    queryKey: [...baseKey(s), 'list', mode(), q, range ? rangeKey(range) : 'all'],
    queryFn: () => {
      if (!range) return api().listCases(s, q)
      const b = rangeBounds(range, Date.now())
      checkCasesRange(b)
      return api().listCases(s, { ...q, createdFrom: b.from, createdTo: b.to })
    },
    enabled,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    retry: 1,
  })
}

/** What a stats window covers, in ms. Home reads `1d`; the listing's picker the rest. */
const STATS_WINDOW_MS = { '1d': 86_400_000, ...WINDOW_MS } as const
export type StatsWindow = keyof typeof STATS_WINDOW_MS

export interface CmStats {
  /** Cases created in the window, by the status category they are in now. */
  counts: Record<CmCategory, number>
  /** Cases created in the window. */
  created: number
  mtta: CmCaseMetrics['mtta']
  mttr: CmCaseMetrics['mttr']
  /** Scope-wide over the window's signals: a mean only, never grouped. */
  mttd: CmCaseMetrics['mttd']
  groups: CmCaseMetrics['groups']
}

/** Category counts from MTTA's status_category groups: measured + excluded = every case created in the window. */
export function statsFrom(m: CmCaseMetrics): CmStats {
  const counts = Object.fromEntries(CM_CATEGORIES.map((c) => [c, 0])) as Record<CmCategory, number>
  for (const g of m.groups.mtta.status_category ?? []) if (g.key && g.key in counts) counts[g.key as CmCategory] += cmMetricCases(g.stats)
  return { counts, created: cmMetricCases(m.mtta), mtta: m.mtta, mttr: m.mttr, mttd: m.mttd, groups: m.groups }
}

/** Why the stats are missing, in the words the screens show (there is no fallback read). */
export function statsErrorLabel(e: unknown): string {
  if (e instanceof ApiError && e.status === 429) return 'Statistics are rate-limited — try again in a moment.'
  if (e instanceof ApiError && e.status === 503) return 'Statistics are busy — try again shortly.'
  return e instanceof Error ? e.message : 'Statistics are unavailable.'
}

/**
 * Every case number the console shows — counts per status category, MTTA,
 * MTTR and the breakdowns by status category, severity, origin and assignee —
 * from ONE `GET /stats/case-metrics?group_by=…` per window. The service
 * counts the cases created in the window (≤ 366 days), not all-time. No
 * fallback: rate-limited (429), busy (503) or too many cases (422) leaves
 * the numbers blank with the reason; those are not retried.
 */
/**
 * The Cases page's summary figures from one case-metrics read (cases created
 * in the window): the CASES card, the header strip and the category chips.
 * Unassigned is the assignee breakdown's null group (MTTA side: every case).
 */
export function statsSummary(s: CmStats): { created: number; new: number; onHold: number; unassigned: number } {
  const unassigned = (s.groups.mtta.assignee ?? []).filter((g) => g.key === null).reduce((n, g) => n + cmMetricCases(g.stats), 0)
  return { created: s.created, new: s.counts.new, onHold: s.counts.on_hold, unassigned }
}

export function useCmStats(s: CmScope, window: StatsWindow | DqlTime) {
  return useQuery<CmStats>({
    queryKey: [...baseKey(s), 'stats', mode(), typeof window === 'string' ? window : rangeKey(window)],
    queryFn: async () => {
      const to = Date.now()
      const b = typeof window === 'string' ? { from: to - STATS_WINDOW_MS[window], to } : rangeBounds(window, to)
      checkCasesRange(b)
      return statsFrom(await api().caseMetrics(s, { ...b, groupBy: CM_METRIC_DIMS }))
    },
    staleTime: 60_000,
    retry: (n, e) => n < 1 && !(e instanceof ApiError && [403, 422, 429, 503].includes(e.status)),
  })
}

/** How many `GET /cases/closed` pages the closure trend reads at once. */
const CLOSED_READS = 4

/**
 * Every closure the query matches, newest first: page 1 gives the total,
 * the rest are read CLOSED_READS at a time. A case closed while reading
 * shifts the pages, so a row seen twice is kept once. Any page failing
 * fails the whole read.
 */
export async function allClosedCases(
  s: CmScope,
  q: { window: CmClosedWindow } | { from?: number; to?: number },
  pageSize = CM_CLOSED_PAGE_MAX,
): Promise<CmPage<CmClosedCase>> {
  const first = await api().closedCases(s, { ...q, page: 1, pageSize })
  const last = Math.ceil(first.total / pageSize)
  const rest: CmClosedCase[][] = []
  let next = 2
  const reader = async () => {
    while (next <= last) {
      const page = next++
      rest[page - 2] = (await api().closedCases(s, { ...q, page, pageSize })).items
    }
  }
  await Promise.all(Array.from({ length: Math.min(CLOSED_READS, last - 1) }, reader))
  const seen = new Set<string>()
  const items = [...first.items, ...rest.flat()].filter((c) => !seen.has(c.caseId) && seen.add(c.caseId))
  return { ...first, items }
}

/**
 * The closure trend's rows: every closure in the page's range
 * (allClosedCases) — `window=` for a preset the service knows (7d · 30d ·
 * 90d), `closed_from`/`closed_to` otherwise — with the bounds the trend
 * buckets over. A range over 366 days is refused before any read, as the
 * statistics refuse it. Viewers get 403; 403, 422, 429 and 503 are not
 * retried.
 */
export function useCmClosedCases(s: CmScope, range: DqlTime) {
  return useQuery<CmPage<CmClosedCase> & { from: number; to: number }>({
    queryKey: [...baseKey(s), 'closed', mode(), rangeKey(range)],
    queryFn: async () => {
      const b = rangeBounds(range, Date.now())
      checkCasesRange(b)
      const preset = range.kind === 'relative' && (CM_CLOSED_WINDOWS as readonly string[]).includes(range.duration)
      const q = preset ? { window: range.duration as CmClosedWindow } : b
      return { ...(await allClosedCases(s, q)), ...b }
    },
    staleTime: 60_000,
    retry: (n, e) => n < 1 && !(e instanceof ApiError && [403, 422, 429, 503].includes(e.status)),
  })
}

export function useCmStatuses(s: CmScope) {
  return useQuery<CmStatus[]>({
    queryKey: [...baseKey(s), 'statuses', mode()],
    queryFn: async () => (await api().statuses(s)).filter((x) => !x.archived).sort((a, b) => a.order - b.order),
    staleTime: 300_000,
    retry: 1,
  })
}

/** The case page's read, keyed by the path id as given — the UUID or `case-N`. */
export const cmWorkspaceKey = (s: CmScope, caseId: string | null) => [...baseKey(s), 'workspace', mode(), caseId]

export function useCmWorkspace(s: CmScope, caseId: string | null) {
  return useQuery<CmWorkspace>({
    queryKey: cmWorkspaceKey(s, caseId),
    queryFn: () => api().workspace(s, caseId!),
    enabled: caseId !== null,
    // A missing case (404) or no access (403) will not change on a retry.
    retry: (n, e) => n < 1 && !(e instanceof ApiError && (e.status === 404 || e.status === 403)),
  })
}

/** Seeds the page read under another path id — moving the URL from the UUID to `case-N` reads nothing again. */
export function useSeedCmWorkspace(s: CmScope) {
  const qc = useQueryClient()
  return (caseId: string, data: CmWorkspace) => qc.setQueryData(cmWorkspaceKey(s, caseId), data)
}

/**
 * Several cases' attached signals — one light workspace read each (signals
 * only, the first CM_SECTIONS_PAGE_MAX: the service's cap) — for the
 * Relations / Clusters graph. The service
 * counts a workspace read as viewing the case (`case.first_viewed`; CB-86).
 */
export function useCmCaseSignals(s: CmScope, caseIds: readonly string[], enabled = true) {
  return useQueries({
    queries: caseIds.map((id) => ({
      queryKey: [...baseKey(s), 'case-signals', mode(), id],
      queryFn: async () => (await api().workspace(s, id, { include: ['signals'], sectionsPageSize: CM_SECTIONS_PAGE_MAX })).signals?.items ?? [],
      enabled,
      staleTime: 60_000,
      retry: (n: number, e: Error) => n < 1 && !(e instanceof ApiError && (e.status === 404 || e.status === 403)),
    })),
  })
}

/**
 * The listing preview: counts and the top three signals only — one light
 * workspace read per case, cached for 30s so moving back through the list
 * re-reads nothing.
 */
export function useCmCasePreview(s: CmScope, caseId: string | null) {
  return useQuery<CmWorkspace>({
    queryKey: [...baseKey(s), 'preview', mode(), caseId],
    queryFn: () => api().workspace(s, caseId!, { include: ['signals'], sectionsPageSize: 3 }),
    enabled: caseId !== null,
    staleTime: 30_000,
    retry: (n, e) => n < 1 && !(e instanceof ApiError && (e.status === 404 || e.status === 403)),
  })
}

/** Assignee picker names: the console users directory (the service has none); demo analysts in demo. */
export function useCmUsers(s: CmScope, enabled = true) {
  return useQuery<string[]>({
    queryKey: ['users', s.clusterId, s.scopeId, 'names', mode()],
    queryFn: async () => {
      if (isDemoSession()) return [...DEMO_ANALYSTS]
      const users = await liveUsersApi.list(s)
      return [...new Set(users.map((u) => u.name || u.email_id).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    },
    enabled,
    staleTime: 300_000,
    retry: 1,
  })
}

/** Create outside a hook — the signals escalation creates, then attaches. */
export const createCmCase = (s: CmScope, input: CmCreateInput) => api().createCase(s, input)

/* ----------------------------------------------------------------- writes */

/** After a case write: everything read about cases, but not the status catalogue (a case write cannot change it). */
function useInvalidate(s: CmScope) {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: baseKey(s), predicate: (q) => q.queryKey[3] !== 'statuses' })
}

export function useUpdateCmCase(s: CmScope) {
  const invalidate = useInvalidate(s)
  return useMutation<CmCase, Error, { caseId: string; patch: CmUpdateInput }>({
    mutationFn: ({ caseId, patch }) => api().updateCase(s, caseId, patch),
    onSuccess: invalidate,
  })
}

/**
 * Change status. Moving to In progress needs an assignee: when the dialog
 * picked one, it is saved first (PATCH), then the transition runs.
 */
export function useTransitionCmCase(s: CmScope) {
  const invalidate = useInvalidate(s)
  return useMutation<void, Error, { caseId: string; input: CmTransitionInput; assignee?: string }>({
    mutationFn: async ({ caseId, input, assignee }) => {
      if (assignee?.trim()) await api().updateCase(s, caseId, { assignee })
      await api().transition(s, caseId, input)
    },
    onSettled: invalidate,
  })
}

export function useBulkCloseCmCases(s: CmScope) {
  const invalidate = useInvalidate(s)
  return useMutation<CmBulkResult, Error, CmBulkCloseInput>({
    mutationFn: (i) => api().bulkClose(s, i),
    onSuccess: invalidate,
  })
}

/** Edit a closed case's disposition. Settled either way: a 409 (reopened elsewhere) shows the case as it is now. */
export function useSetCmDisposition(s: CmScope) {
  const invalidate = useInvalidate(s)
  return useMutation<CmDispositionChange, Error, { caseId: string; disposition: CmDisposition }>({
    mutationFn: ({ caseId, disposition }) => api().setDisposition(s, caseId, disposition),
    onSettled: invalidate,
  })
}

export interface AttachOutcome {
  attached: string[]
  /** Per signal id: the server's reason. */
  failed: { signalId: string; error: string }[]
}

/** Attach signals one call each (the service takes one per request); keeps going past a failure. */
export async function attachSignalsTo(s: CmScope, caseId: string, signalIds: string[], note?: string): Promise<AttachOutcome> {
  const out: AttachOutcome = { attached: [], failed: [] }
  for (const signalId of signalIds) {
    try {
      await api().attachSignal(s, caseId, { signalId, note })
      out.attached.push(signalId)
    } catch (e) {
      out.failed.push({ signalId, error: e instanceof Error ? e.message : 'attach failed' })
    }
  }
  return out
}

export function useAttachCmSignals(s: CmScope) {
  const invalidate = useInvalidate(s)
  return useMutation<AttachOutcome, Error, { caseId: string; signalIds: string[]; note?: string }>({
    mutationFn: ({ caseId, signalIds, note }) => attachSignalsTo(s, caseId, signalIds, note),
    onSettled: invalidate,
  })
}

/**
 * The cases as the service exports them (`POST /cases/export`), in the order
 * given: one request per CM_EXPORT_MAX cases, one after another; any failure
 * fails the export.
 */
export async function exportCmCases(s: CmScope, ids: readonly string[], onProgress?: (done: number) => void): Promise<CmCaseExport['cases']> {
  const out: CmCaseExport['cases'] = []
  for (let i = 0; i < ids.length; i += CM_EXPORT_MAX) {
    out.push(...(await api().exportCases(s, ids.slice(i, i + CM_EXPORT_MAX))).cases)
    onProgress?.(Math.min(i + CM_EXPORT_MAX, ids.length))
  }
  return out
}

/** The list's biggest page (`page_size` ≤ 200). */
const LIST_PAGE_MAX = 200

/**
 * Every case the listing's filters (and range, read as the list reads it)
 * match, all pages, in the service's order — for Export all.
 */
export async function allCases(s: CmScope, q: Omit<CmListQuery, 'page' | 'pageSize'>, range?: DqlTime, pageSize = LIST_PAGE_MAX): Promise<CmCase[]> {
  const b = range ? rangeBounds(range, Date.now()) : null
  const out: CmCase[] = []
  for (let page = 1; ; page++) {
    const p = await api().listCases(s, { ...q, page, pageSize, ...(b ? { createdFrom: b.from, createdTo: b.to } : {}) })
    out.push(...p.items)
    if (!p.items.length || out.length >= p.total) return out
  }
}
